import produce from 'immer'
import { combineReducers } from 'redux'
import {
  getAllPressureMonitorsAction,
  getPressureMonitorAction,
  getAllAvailablePressureMonitorsBySiteAction,
  getPressureMonitorEditHistoryAction,
  getPressureMonitorAssignmentHistoryAction,
  getPressureMonitorDataAction,
} from '../actions/pressureMonitorsActions'
import { showErrorMessageAction } from '../actions/uiActions'
import { pickErrorMessage } from '../utils/helpers'
import { toTitleCase } from '../utils/textFormatters'

const byId = (state = {}, action) =>
  produce(state, draft => {
    const { type, payload } = action

    switch (type) {
      case getAllPressureMonitorsAction.SUCCESS:
        payload.items.forEach(pressureMonitor => {
          draft[pressureMonitor.id] = pressureMonitor
        })
        break
      case getAllAvailablePressureMonitorsBySiteAction.SUCCESS:
        payload.forEach(pressureMonitor => {
          draft[pressureMonitor.id] = pressureMonitor
        })
        break
    }
  })

const visibleIds = (state = [], action) =>
  produce(state, draft => {
    switch (action.type) {
      case getAllPressureMonitorsAction.SUCCESS:
        draft.splice(
          0,
          draft.length,
          ...action.payload.items.map(pressureMonitor => pressureMonitor.id)
        )
        break
    }
  })

const availableVisibleIds = (state = [], action) =>
  produce(state, draft => {
    switch (action.type) {
      case getAllAvailablePressureMonitorsBySiteAction.SUCCESS:
        draft.splice(
          0,
          draft.length,
          ...action.payload.map(pressureMonitor => pressureMonitor.id)
        )
        break
    }
  })

const meta = (state = {}, action) =>
  produce(state, draft => {
    switch (action.type) {
      case getAllPressureMonitorsAction.SUCCESS:
        Object.assign(draft, action.payload.meta)
        break
    }
  })

const current = (state = {}, action) =>
  produce(state, draft => {
    switch (action.type) {
      case getPressureMonitorAction.SUCCESS:
        Object.assign(draft, action.payload)
        break
    }
  })

const editHistory = (state = [], action) =>
  produce(state, draft => {
    switch (action.type) {
      case getPressureMonitorEditHistoryAction.SUCCESS:
        const editHistory = action.payload.items
        const newEditHistory = []

        if (editHistory.length) {
          editHistory.forEach((row, i) => {
            const { updatedBy, date, changedFields } = row
            let changes
            try {
              changes = JSON.parse(changedFields)
            } catch (error) {
              showErrorMessageAction(pickErrorMessage(error))
            }

            // each row has an updatedBy, date, field changed, to, from
            if (changes) {
              Object.keys(changes).forEach((field, j) => {
                let to = changes[field].to
                let from = changes[field].from

                try {
                  to = JSON.parse(to)
                } catch (error) {
                  if (!to) {
                    to = '--'
                  } else if (field === 'status') {
                    to = toTitleCase(to)
                  } else {
                    showErrorMessageAction(pickErrorMessage(error))
                  }
                }

                try {
                  from = JSON.parse(from)
                } catch (error) {
                  if (!from) {
                    from = '--'
                  } else if (field === 'status') {
                    from = toTitleCase(from)
                  } else {
                    showErrorMessageAction(pickErrorMessage(error))
                  }
                }

                // unique IDs are hard to come by...
                const tableId = i.toString() + j.toString()

                newEditHistory.push({
                  updatedBy,
                  date,
                  field,
                  to,
                  from,
                  tableId,
                })
              })
            }
          })
        }

        draft.splice(0, draft.length, ...newEditHistory)
        break
    }
  })

const assignmentHistory = (state = [], action) =>
  produce(state, draft => {
    switch (action.type) {
      case getPressureMonitorAssignmentHistoryAction.SUCCESS:
        draft.splice(0, draft.length, ...action.payload.items)
        break
    }
  })

const chartData = (state = {}, action) =>
  produce(state, draft => {
    switch (action.type) {
      case getPressureMonitorDataAction.SUCCESS:
        Object.assign(draft, action.payload)
        break
    }
  })

const pressureMonitorsReducer = combineReducers({
  byId,
  visibleIds,
  availableVisibleIds,
  current,
  editHistory,
  assignmentHistory,
  chartData,
  meta,
})

const getPressureMonitor = (state, id) => state.byId[id]
const getCurrentPressureMonitor = state => state.current
const getVisiblePressureMonitors = state =>
  state.visibleIds.map(id => getPressureMonitor(state, id))
const getAvailableVisiblePressureMonitors = state =>
  state.availableVisibleIds.map(id => getPressureMonitor(state, id))
const getPressureMonitorsMeta = state => state.meta
const getVisiblePressureMonitorEditHistory = state => state.editHistory
const getVisiblePressureMonitorAssignmentHistory = state =>
  state.assignmentHistory
const getVisiblePressureMonitorData = state => state.chartData

export {
  pressureMonitorsReducer as default,
  getCurrentPressureMonitor,
  getVisiblePressureMonitors,
  getAvailableVisiblePressureMonitors,
  getPressureMonitorsMeta,
  getVisiblePressureMonitorEditHistory,
  getVisiblePressureMonitorAssignmentHistory,
  getVisiblePressureMonitorData,
}
